import React, { useEffect } from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";
import { ReactComponent as SearchIcon } from "assets/icons/search-white.svg";
import { Alert, Pagination } from "@material-ui/lab";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { SearchButton } from "components/searchButton";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";
import { UltimaAtualizacao } from "components/ultimaAtualizacao";
import { ExportButtons } from "components/exportButtons";
import { StatCards } from "components/statCards";
import { SortDirectionSelect } from "components/sortDirectionSelect";
import { useEmendasParlamentaresState } from "./context/emendas-parlamentares-context";
import { useEmendasParlamentaresController } from "./context/emendas-parlamentares-controller";
import { EmendasParlamentaresTable } from "./emendasParlamentaresTable.component";

const formatCurrency = (value?: number) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function EmendasParlamentaresPage() {
  const { emendas, emendaPagedRequest, loading, tipos, formasRepasse, ultimaAtualizacao, totais, error } = useEmendasParlamentaresState();
  const { onInit, handleOnChange, listEmendas, exportEmendas } = useEmendasParlamentaresController();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await onInit();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick = (event: React.ChangeEvent<unknown>, value: number) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      await listEmendas({ ...emendaPagedRequest, page: value - 1 }, true);
    })();
  };

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Emendas Parlamentares" },
        ]}
      />
      <PageTitle>Emendas Parlamentares</PageTitle>
      <Wrapper>
        <StatCards
          items={[
            { label: "Valor Total Previsto", value: formatCurrency(totais?.valorTotalPrevisto), variant: 'green' },
            { label: "Valor Total Realizado", value: formatCurrency(totais?.valorTotalRealizado), variant: 'blue' },
          ]}
        />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name="text-field-term"
                  label="Número, autor ou objeto"
                  value={emendaPagedRequest?.term || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  variant="outlined"
                  fullWidth
                  name="text-field-ano"
                  label="Exercício"
                  value={emendaPagedRequest?.ano || ''}
                  onChange={(e) => handleOnChange(e)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Modalidade Emenda</InputLabel>
                  <Select
                    name="select-tipo-id"
                    value={emendaPagedRequest?.idTipoEmenda || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Modalidade Emenda"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {tipos?.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Formas de Repasse</InputLabel>
                  <Select
                    name="select-forma-repasse-id"
                    value={emendaPagedRequest?.idFormaRepasseEmenda || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Formas de Repasse"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {formasRepasse?.map((f) => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel shrink>Tipo de Origem</InputLabel>
                  <Select
                    name="select-tipo-origem"
                    value={emendaPagedRequest?.tipoOrigem || ''}
                    onChange={(e) => handleOnChange(e)}
                    label="Tipo de Origem"
                    displayEmpty
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Federal">Federal</MenuItem>
                    <MenuItem value="Estadual">Estadual</MenuItem>
                    <MenuItem value="Municipal">Municipal</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <SearchButton onClick={() => (async () => {
                  await listEmendas(emendaPagedRequest, true);
                })()}>
                  <SearchIcon /> Consultar
                </SearchButton>
              </Grid>
              <Grid item xs={12} sm={3}>
                <SortDirectionSelect
                  value={emendaPagedRequest?.orderDirection}
                  onChange={(e) => handleOnChange(e)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CustomBackdrop open={loading} />
            <UltimaAtualizacao data={ultimaAtualizacao} />
            <ExportButtons onExport={exportEmendas} />
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <EmendasParlamentaresTable currentItems={emendas} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3} justifyContent="center">
              <Pagination
                size="large"
                variant="outlined"
                shape="rounded"
                onChange={handlePageClick}
                count={emendas?.pageCount}
                color="primary"
              />
            </Grid>
          </Grid>
        </Grid>
      </Wrapper>
    </>
  );
}
